import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { legacyApi, SaleOrder } from '../../../services/legacyApi';
import { CompactTable } from '../../../components/ui/CompactTable';
import { useConfirm } from '../../../hooks/use-confirm';
import { toast } from 'sonner';

import { Button } from '../../../components/ui/button';
import { Eye, Pencil, Trash2, Download } from 'lucide-react';
import { StandardActions } from '../components/StandardActions';
import { getSaleOrderStatusMeta } from '../utils/statuses';
import { TableFilterBar } from '../../../components/table-filters/TableFilterBar';
import { useTableFilterState } from '../../../hooks/useTableFilterState';
import { FlowEntryBanner } from '../../../components/workflow/FlowEntryBanner';
import { useDebouncedFilterValue } from '../../../hooks/useDebouncedFilterValue';

export const LegacySaleOrders: React.FC = () => {
    const PAGE_SIZE = 50;
    const navigate = useNavigate();
    const { confirm } = useConfirm();
    const [orders, setOrders] = useState<SaleOrder[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
    const [debouncedQ, setDebouncedQ] = useState('');
    const { state, setFilter, clearAll, isDirty } = useTableFilterState({
        q: '',
        status: 'all',
        paymentStatus: 'all',
        fulfillment: 'all',
        dateRange: '|'
    });
    const [searchValue, setSearchValue] = useDebouncedFilterValue(state.q, (value) => setFilter('q', value));

    const [searchParams] = useSearchParams();
    const estimateIdFilter = searchParams.get('estimate_id');

    const formatSoNumber = (o: SaleOrder) => o.so_number || `HC/SO/${String(o.so_id).padStart(4, '0')}`;
    const formatEstimateNumber = (o: SaleOrder) => {
        if (!o.estimate_id || o.estimate_id <= 0) return '-';
        return o.estimate_ref || o.estimate_number || `HC/EST/${String(o.estimate_id).padStart(4, '0')}`;
    };

    useEffect(() => {
        const t = window.setTimeout(() => setDebouncedQ(state.q.trim()), 250);
        return () => window.clearTimeout(t);
    }, [state.q]);

    const loadOrders = async (reset: boolean = true) => {
        const nextOffset = reset ? 0 : orders.length;
        if (reset) setLoading(true);
        else setLoadingMore(true);
        const [start, end] = state.dateRange.split('|');
        try {
            const response = await legacyApi.sales.listOrdersPaged({
                estimate_id: estimateIdFilter ? Number(estimateIdFilter) : undefined,
                q: debouncedQ || undefined,
                status_id: state.status !== 'all' ? Number(state.status) : undefined,
                payment_status: state.paymentStatus !== 'all' ? state.paymentStatus : undefined,
                pending_dispatch: state.fulfillment === 'pending_dispatch',
                date_from: start || undefined,
                date_to: end || undefined,
                limit: PAGE_SIZE,
                offset: nextOffset,
            });
            setOrders((prev) => (reset ? response.items : [...prev, ...response.items]));
            setTotalCount(response.total);
            setHasMore(response.has_more);
        } catch (err) {
            console.error(err);
        } finally {
            if (reset) {
                setLoading(false);
                setHasLoadedOnce(true);
            }
            else setLoadingMore(false);
        }
    };

    useEffect(() => {
        loadOrders(true);
    }, [estimateIdFilter, debouncedQ, state.status, state.paymentStatus, state.fulfillment, state.dateRange]);

    if (!hasLoadedOnce && loading) return <div className="p-4">Loading Sale Orders...</div>;

    const handleDelete = async (soId: number) => {
        const confirmed = await confirm(`Delete ${`HC/SO/${String(soId).padStart(4, '0')}`}?`, {
            description: 'This action cannot be undone.',
            confirmText: 'Delete',
            variant: 'destructive'
        });
        if (!confirmed) return;
        try {
            await legacyApi.sales.deleteOrder(soId);
            setOrders(prev => prev.filter(o => o.so_id !== soId));
            setTotalCount(prev => Math.max(prev - 1, 0));
        } catch (e: any) {
            toast.error(e?.message || 'Failed to delete sale order');
        }
    };

    const getStatusBadge = (statusId: number, statusName: string) => {
        const statusMeta = getSaleOrderStatusMeta(statusId, statusName);

        return (
            <span className={`px-2 py-1 rounded text-xs ${statusMeta.badgeClass} font-medium inline-block min-w-[96px] text-center whitespace-nowrap`}>
                {statusMeta.label}
            </span>
        );
    };

    const getProgressBadge = (kind: 'dispatch' | 'invoice', progressId?: number, progressName?: string) => {
        const id = Number(progressId || 1);
        const label =
            progressName
            || (kind === 'dispatch'
                ? (id === 3 ? 'Fully Dispatched' : id === 2 ? 'Partially Dispatched' : 'Not Dispatched')
                : (id === 3 ? 'Fully Invoiced' : id === 2 ? 'Partially Invoiced' : 'Not Invoiced'));
        const cls =
            id === 3
                ? 'bg-emerald-100 text-emerald-700'
                : id === 2
                    ? 'bg-amber-100 text-amber-800'
                    : 'bg-slate-100 text-slate-600';
        return (
            <span className={`px-2 py-1 rounded text-xs font-medium inline-block min-w-[128px] text-center whitespace-nowrap ${cls}`}>
                {label}
            </span>
        );
    };

    const getPaymentStatusBadge = (status?: SaleOrder['payment_status']) => {
        const label = status || 'Not Invoiced';
        let cls = 'bg-slate-100 text-slate-600';
        if (label === 'Paid') cls = 'bg-emerald-100 text-emerald-700';
        else if (label === 'Partially Paid') cls = 'bg-amber-100 text-amber-800';
        else if (label === 'Pending') cls = 'bg-blue-100 text-blue-700';
        else if (label === 'Draft') cls = 'bg-indigo-100 text-indigo-700';
        else if (label === 'Cancelled') cls = 'bg-red-100 text-red-700';
        return (
            <span className={`px-2 py-1 rounded text-xs font-medium inline-block min-w-[112px] text-center whitespace-nowrap ${cls}`}>
                {label}
            </span>
        );
    };


    const columns = [
        { header: "SO ID", accessor: (o: SaleOrder) => formatSoNumber(o), className: 'font-mono whitespace-nowrap w-32' },
        {
            header: "Estimate",
            accessor: (o: SaleOrder) => (o.estimate_id && o.estimate_id > 0 ? (
                <Link
                    to={`/legacy/estimates/${o.estimate_id}`}
                    onClick={(e) => e.stopPropagation()}
                    className="font-mono text-blue-600 hover:underline"
                >
                    {formatEstimateNumber(o)}
                </Link>
            ) : (
                <span className="text-muted-foreground">Direct SO</span>
            )),
            className: 'whitespace-nowrap w-32'
        },
        { header: "Customer", accessor: (o: SaleOrder) => o.customer_name || '-', className: 'min-w-[200px] max-w-[300px] truncate' },
        { header: "SO Date", accessor: (o: SaleOrder) => o.so_date ? new Date(o.so_date).toLocaleDateString() : '-', className: 'w-24 whitespace-nowrap' },
        { header: "Customer PO#", accessor: (o: SaleOrder) => o.customer_po_no || '-', className: 'min-w-[150px] max-w-[200px] truncate' },
        { header: "PO Issue Date", accessor: (o: SaleOrder) => o.po_issue_date ? new Date(o.po_issue_date).toLocaleDateString() : '-', className: 'w-24 whitespace-nowrap' },
        { header: "SO Status", accessor: (o: SaleOrder) => getStatusBadge(o.so_status_id || 1, o.status_name || 'Open'), className: 'w-24 text-center' },
        { header: "Dispatch", accessor: (o: SaleOrder) => getProgressBadge('dispatch', o.dispatch_progress_id, o.dispatch_progress_name), className: 'w-36 text-center' },
        { header: "Invoice", accessor: (o: SaleOrder) => getProgressBadge('invoice', o.invoice_progress_id, o.invoice_progress_name), className: 'w-36 text-center' },
        { header: "Payment", accessor: (o: SaleOrder) => getPaymentStatusBadge(o.payment_status), className: 'w-36 text-center' },
        { header: "Order Amount", accessor: (o: SaleOrder) => (o.total_amount ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }), className: 'w-28 text-right font-medium tabular-nums' },
        {
            header: "Actions",
            accessor: (o: SaleOrder) => (
                <StandardActions
                    view={{
                        title: "View Details",
                        onClick: (e) => { e.stopPropagation(); navigate(`/legacy/orders/${o.so_id}`); },
                        icon: <Eye className="w-4 h-4 text-green-600" />
                    }}
                    edit={{
                        title: "Edit Sale Order",
                        onClick: (e) => { e.stopPropagation(); navigate(`/legacy/orders/${o.so_id}?edit=1`); },
                        icon: <Pencil className="w-4 h-4 text-blue-600" />
                    }}
                    download={{
                        title: "Download PDF",
                        onClick: async (e) => { e.stopPropagation(); await legacyApi.sales.downloadPdf(o.so_id); },
                        icon: <Download className="w-4 h-4 text-purple-600" />
                    }}
                    remove={{
                        title: "Delete Sale Order",
                        onClick: (e) => { e.stopPropagation(); handleDelete(o.so_id); },
                        icon: <Trash2 className="w-4 h-4 text-red-600" />
                    }}
                />
            ),
            className: 'text-right'
        }
    ];

    return (
        <div className="h-full flex flex-col space-y-6">
            <div className="flex items-center justify-between space-y-2">
                <div>
                    <h2 className="text-3xl font-bold tracking-tight text-foreground">Sale Orders</h2>
                    <p className="text-muted-foreground">Manage and view all legacy sale orders - showing {orders.length} of {totalCount}</p>
                </div>
                <Button onClick={() => navigate('/legacy/orders/new')}>Create Sale Order</Button>
            </div>
            <div className="space-y-4">
                <FlowEntryBanner
                    title="Sales Path Guidance"
                    pathLabel="Standard Sales / Direct SO"
                    nextHint="Track order lifecycle here. Dispatch delivery before creating final invoice quantities."
                />
                <div className="flex items-center gap-2">
                    <TableFilterBar
                        searchValue={searchValue}
                        onSearchChange={setSearchValue}
                        searchPlaceholder="Search by SO, estimate, customer, PO..."
                        controls={[
                            {
                                key: 'dateRange',
                                label: 'Date',
                                type: 'daterange',
                                value: state.dateRange,
                                onChange: (value) => setFilter('dateRange', value)
                            },
                            {
                                key: 'status',
                                label: 'Status',
                                type: 'select',
                                value: state.status,
                                onChange: (value) => setFilter('status', value),
                                options: [
                                    { value: 'all', label: 'All Statuses' },
                                    { value: '1', label: 'Draft' },
                                    { value: '2', label: 'Confirmed' },
                                    { value: '3', label: 'In Production' },
                                    { value: '4', label: 'Ready to Ship' },
                                    { value: '5', label: 'Dispatched' },
                                    { value: '6', label: 'Closed' },
                                    { value: '7', label: 'Cancelled' }
                                ]
                            },
                            {
                                key: 'paymentStatus',
                                label: 'Payment',
                                type: 'select',
                                value: state.paymentStatus,
                                onChange: (value) => setFilter('paymentStatus', value),
                                options: [
                                    { value: 'all', label: 'All Payment Status' },
                                    { value: 'not_invoiced', label: 'Not Invoiced' },
                                    { value: 'draft', label: 'Draft' },
                                    { value: 'pending', label: 'Pending' },
                                    { value: 'partially_paid', label: 'Partially Paid' },
                                    { value: 'paid', label: 'Paid' },
                                    { value: 'cancelled', label: 'Cancelled' }
                                ]
                            },
                            {
                                key: 'fulfillment',
                                label: 'Fulfillment',
                                type: 'chips',
                                value: state.fulfillment,
                                onChange: (value) => setFilter('fulfillment', value),
                                options: [
                                    { value: 'all', label: 'All Orders' },
                                    { value: 'pending_dispatch', label: 'Pending Dispatch' }
                                ]
                            }
                        ]}
                        isDirty={isDirty}
                        onClear={clearAll}
                    />
                </div>
                <div className="flex-1 overflow-auto rounded-md border">
                    <>
                        <CompactTable
                            data={orders}
                            columns={columns}
                            keyField="so_id"
                            loading={loading}
                            onRowClick={(row) => navigate(`/legacy/orders/${row.so_id}`)}
                        />
                        {hasMore && (
                            <div className="flex justify-center py-4">
                                <Button variant="ghost" onClick={() => loadOrders(false)} disabled={loadingMore}>
                                    {loadingMore ? 'Loading...' : 'Load older sale orders'}
                                </Button>
                            </div>
                        )}
                    </>
                </div>
            </div>
        </div>
    );
};
